import * as React from 'react';
import { useEffect, useState } from 'react';
import CompanyInformation from '../../molecules/CompanyInformation';
import Typeahead from '../../molecules/Typeahead';
import BusinessDetailsProps, { Company, Option } from './BusinessDetails.types';
import { CompanyInformationProps } from '../../molecules/CompanyInformation/CompanyInformation.types';
import styles from './BusinessDetails.module.scss';

const BusinessDetails = ({
  searchUrl = '',
  sicCodesSearchUrl,
  businessStartDateErrorMessage,
  placeholder,
  description,
  emptyOption = 'No match',
  onChange,
  serviceCaller,
  dataPointer,
  dataPointerOverride,
  dataCy = 'businessDetails',
  editBtnTrackingLabel = 'company-information-edit',
  invalid,
  lookupAriaLabel,
  errorMsgHolderId,
  minSearchLength = 1,
  debounceDelay = 500,
  loadingText = 'Loading',
  tradingNameKey = 'tradingName',
  registeredAddressKey = 'registeredAddress',
}: BusinessDetailsProps) => {
  const [companyInformationProps, setCompanyInformationProps] = useState<CompanyInformationProps>(null);

  const [isEditStartDate, setIsEditStartDate] = useState<boolean>(false);

  useEffect(() => {
    const value = companyInformationProps
      ? {
          [tradingNameKey]: companyInformationProps.tradingName,
          registrationNumber: companyInformationProps.registrationNumber,
          [registeredAddressKey]: { ...companyInformationProps.registeredAddress },
          businessStartDate: companyInformationProps.businessStartDate,
          sicCodes: companyInformationProps.sicCodes,
        }
      : {
          [tradingNameKey]: null,
          registrationNumber: null,
          [registeredAddressKey]: {},
          businessStartDate: null,
          sicCodes: [],
        };

    onChange({
      value,
      dataPointer: dataPointerOverride || dataPointer,
    });

    const regNumber = value?.registrationNumber;
    if (regNumber) {
      onChange({ value: regNumber, dataPointer: '/registrationNumber' });
    }
  }, [companyInformationProps]);

  useEffect(() => {
    if (!isEditStartDate) return;

    const submitButton = document.querySelector<HTMLButtonElement>('button[type="submit"][data-edit-text]');

    if (submitButton) {
      submitButton.innerText = submitButton.getAttribute('data-edit-text');
    }
  }, [isEditStartDate]);

  const handleIncorrectClick = () => {
    setIsEditStartDate(true);
  };

  const handleSicCodesResponse = (selectedCompany: Company) => (response: { data: { sicCodes: string[] } }) => {
    const { dateOfCreation: businessStartDate, address, name, companyNumber: registrationNumber } = selectedCompany;

    const sicCodes: string[] = response?.data?.sicCodes || [];

    setCompanyInformationProps({
      tradingName: name,
      registrationNumber: `${registrationNumber}`,
      businessStartDate,
      businessStartDateErrorMessage,
      registeredAddress: {
        postCode: address.postalCode,
        line1: address.addressLine1,
        line2: address.addressLine2,
        city: address.city,
        country: address.country,
      },
      sicCodes,
    });
  };

  const formatSelection = (selectedCompany: Company) => {
    if (selectedCompany) {
      const { companyNumber: registrationNumber } = selectedCompany;
      serviceCaller({
        url: sicCodesSearchUrl,
        method: 'get',
        query: {
          companyNumber: registrationNumber,
        },
        onSuccessCallback: handleSicCodesResponse(selectedCompany),
      });
    } else {
      setCompanyInformationProps(undefined);
    }
  };

  const handleDateChange = (date: string) => {
    const clonedProps = { ...companyInformationProps };
    clonedProps.businessStartDate = date;
    setCompanyInformationProps(clonedProps);
  };

  const CompanyInformationComponent = companyInformationProps && (
    <CompanyInformation
      {...companyInformationProps}
      isEdit={isEditStartDate}
      onDateChange={handleDateChange}
      onIncorrectClick={handleIncorrectClick}
      incorrectTrackingLabel={editBtnTrackingLabel}
    />
  );

  const DescriptionComponent = companyInformationProps && (
    <p className={styles.businessDetailsDescription} aria-live='polite'>
      {description}
    </p>
  );

  const createOptions = (companies) => {
    if (companies) {
      const newOptions = companies.map((company) => {
        return {
          title: company.name,
          params: {
            ...company,
          },
        };
      });
      return newOptions;
    }
    return [];
  };

  const filterFunction = async (search: string): Promise<Option[]> => {
    formatSelection(null);

    const lookupResponse = serviceCaller({
      url: searchUrl,
      method: 'get',
      query: {
        companyName: search,
      },
    });

    const { data } = await lookupResponse;
    return createOptions(data?.companies);
  };

  const handleSelection = (option) => {
    if (option.params) formatSelection(option.params);
  };

  return (
    <>
      <div className={styles.businessDetailsLookup}>
        <Typeahead
          filterFunction={filterFunction}
          onChange={handleSelection}
          noMatchText={emptyOption}
          dataCy={dataCy}
          placeholder={placeholder}
          invalid={invalid}
          ariaLabel={lookupAriaLabel}
          errorMsgHolderId={errorMsgHolderId}
          minSearchLength={minSearchLength}
          debounceDelay={debounceDelay}
          loadingText={loadingText}
        />
      </div>
      {DescriptionComponent}
      <div className={styles.businessDetailsInformation} aria-live='polite'>
        {CompanyInformationComponent}
      </div>
    </>
  );
};

export default BusinessDetails;
